import React from 'react';
import { Add, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { Button, IconButton, styled, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';

// "1 médico cadastrado" / "3 médicos cadastrados" / "Nenhum médico cadastrado"
export function countLabel(count: number, singular: string, plural: string, empty: string) {
  if (count === 0) return empty;
  return `${count} ${count === 1 ? singular : plural}`;
}

const Toolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
}));

interface RecordToolbarProps {
  title: string;
  addLabel: string;
  onAdd(): void;
}

export const RecordToolbar: React.FC<RecordToolbarProps> = ({ title, addLabel, onAdd }) => (
  <Toolbar>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>
    <Button variant="contained" size="small" startIcon={<Add />} onClick={onAdd}>
      {addLabel}
    </Button>
  </Toolbar>
);

export interface RecordColumn {
  id: string;
  label: string;
  // rótulo curto usado no celular (ex.: "Mín" no lugar de "Mínimo")
  shortLabel?: string;
  // colunas numéricas: alinhadas à direita, largura fixa
  align?: 'left' | 'right';
  width?: string;
}

export interface RecordRow {
  key: string | number;
  values: Record<string, string>;
}

const Table = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

const Line = styled('div')(({ theme }) => ({
  display: 'grid',
  alignItems: 'center',
  columnGap: theme.spacing(2),
  padding: theme.spacing(1.25, 1.75),
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '&.body:hover': {
    backgroundColor: theme.palette.grey[50],
  },
  '&.head': {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(0.75, 1.75),
  },
}));

const Number = styled('span')({
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

const Actions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

interface RecordListProps {
  columns: RecordColumn[];
  rows: RecordRow[];
  onEdit(index: number): void;
  onDelete(index: number): void;
}

const RowActions: React.FC<{ index: number; onEdit(i: number): void; onDelete(i: number): void }> = ({
  index,
  onEdit,
  onDelete,
}) => (
  <Actions>
    <Tooltip title="Editar">
      <IconButton size="small" color="primary" aria-label="Editar" onClick={() => onEdit(index)}>
        <EditOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Excluir">
      <IconButton size="small" color="error" aria-label="Excluir" onClick={() => onDelete(index)}>
        <DeleteOutline fontSize="small" />
      </IconButton>
    </Tooltip>
  </Actions>
);

// Colunas alinhadas com cabeçalho em telas largas; abaixo de 900px (celular/tablet) vira nome + ações e, abaixo, o resumo dos demais campos.
export const RecordList: React.FC<RecordListProps> = ({ columns, rows, onEdit, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [primary, ...others] = columns;
  const textColumns = others.filter(column => column.align !== 'right');
  const numberColumns = others.filter(column => column.align === 'right');

  if (isMobile) {
    return (
      <Table>
        {rows.map((row, index) => (
          <Line key={row.key} className="body" style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
            <div style={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'break-word' }}>
                {row.values[primary.id]}
              </Typography>
              {textColumns.length > 0 && (
                <Typography variant="caption" color="textSecondary" noWrap display="block">
                  {textColumns.map(column => row.values[column.id]).join(' · ')}
                </Typography>
              )}
            </div>
            <RowActions index={index} onEdit={onEdit} onDelete={onDelete} />
            {numberColumns.length > 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ gridColumn: '1 / -1' }}>
                {numberColumns.map((column, columnIndex) => (
                  <React.Fragment key={column.id}>
                    {columnIndex > 0 && ' · '}
                    {column.shortLabel || column.label}{' '}
                    <span style={{ color: theme.palette.text.primary }}>{row.values[column.id]}</span>
                  </React.Fragment>
                ))}
              </Typography>
            )}
          </Line>
        ))}
      </Table>
    );
  }

  const template = [
    'minmax(0, 2fr)',
    ...others.map(column => (column.align === 'right' ? column.width || '120px' : 'minmax(0, 1.1fr)')),
    '72px',
  ].join(' ');

  return (
    <Table>
      <Line className="head" style={{ gridTemplateColumns: template }}>
        {columns.map((column, columnIndex) =>
          column.align === 'right' ? (
            <Number key={column.id}>
              <Typography variant="caption" color="textSecondary">
                {column.label}
              </Typography>
            </Number>
          ) : (
            <Typography key={column.id} variant="caption" color="textSecondary" noWrap>
              {column.label}
            </Typography>
          ),
        )}
        <span />
      </Line>

      {rows.map((row, index) => (
        <Line key={row.key} className="body" style={{ gridTemplateColumns: template }}>
          <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'break-word' }}>
            {row.values[primary.id]}
          </Typography>
          {others.map(column =>
            column.align === 'right' ? (
              <Number key={column.id}>
                <Typography variant="body2" component="span">
                  {row.values[column.id]}
                </Typography>
              </Number>
            ) : (
              <Typography key={column.id} variant="body2" color="textSecondary" noWrap>
                {row.values[column.id]}
              </Typography>
            ),
          )}
          <RowActions index={index} onEdit={onEdit} onDelete={onDelete} />
        </Line>
      ))}
    </Table>
  );
};
